import axios from 'axios';
import { normalizeApiError } from './errorUtils';
import { startGlobalLoader, stopGlobalLoader } from '../utils/globalLoaderBus';

const normalizeBaseUrl = (url) => {
  if (!url) return 'https://api.skhool.in/';
  return url.endsWith('/') ? url : `${url}/`;
};

const resolveBaseUrl = () => {
  const configured = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL);

  if (typeof window === 'undefined') return configured;

  const appHost = String(window.location?.hostname || '').toLowerCase();
  const isAppLocal = appHost === 'localhost' || appHost === '127.0.0.1';
  const isConfiguredLocal =
    configured.includes('://localhost:') ||
    configured.includes('://127.0.0.1:') ||
    configured.startsWith('http://localhost') ||
    configured.startsWith('http://127.0.0.1');

  if (!isAppLocal && isConfiguredLocal) {
    return 'https://api.skhool.in/';
  }

  return configured;
};

const BASE_URL = resolveBaseUrl();

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

const emitGlobalApiError = (normalizedError) => {
  if (typeof window === 'undefined' || !normalizedError) return;
  window.dispatchEvent(new CustomEvent('app:api-error', { detail: normalizedError }));
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (!config.skipGlobalLoader) {
      config.__globalLoaderTracked = true;
      startGlobalLoader();
    }

    if (!config.skipAuthHeaders) {
      const customHeaders = {
        'x-api-key': localStorage.getItem('x-api-key'),
        userType: localStorage.getItem('userType'),
        code: localStorage.getItem('code'),
        name: `${localStorage.getItem('firstName') || ''} ${localStorage.getItem('lastName') || ''}`.trim(),
        role: localStorage.getItem('role'),
      };

      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      Object.entries(customHeaders).forEach(([key, value]) => {
        if (value) {
          config.headers[key] = value;
        }
      });
    }

    const storedSchoolId = localStorage.getItem('schoolId');
    if (!config.skipAuthHeaders && !config.headers.schoolId && storedSchoolId && /^\d+$/.test(String(storedSchoolId).trim())) {
      config.headers.schoolId = String(storedSchoolId).trim();
    }

    if (config.headers.schoolId && !/^\d+$/.test(String(config.headers.schoolId).trim())) {
      delete config.headers.schoolId;
    }

    return config;
  },
  (error) => {
    if (error?.config?.__globalLoaderTracked) {
      stopGlobalLoader();
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (response?.config?.__globalLoaderTracked) {
      stopGlobalLoader();
    }
    return response;
  },
  (error) => {
    if (error?.config?.__globalLoaderTracked) {
      stopGlobalLoader();
    }

    const normalizedError = normalizeApiError(error, 'Unable to process your request.');
    error.normalizedError = normalizedError;

    if (error.response?.status === 401) {
      const skipAuthRedirect = Boolean(error.config?.skipAuthRedirect);
      if (
        !skipAuthRedirect &&
        typeof window !== 'undefined' &&
        window.location.pathname !== '/login'
      ) {
        localStorage.clear();
        window.location.replace('/login');
      }
      return Promise.reject(error);
    }

    if (!error.config?.skipGlobalError) {
      emitGlobalApiError(normalizedError);
    }

    return Promise.reject(error);
  }
);

axiosInstance.uploadImage = async (file, imageType = 'IMAGES', schoolId = null, description = null, categoryName = null) => {
  if (!file) {
    throw new Error('File is required for upload.');
  }

  const resolvedSchoolId = schoolId || localStorage.getItem('schoolId');
  const resolvedCategoryName =
    categoryName || (imageType && imageType !== 'IMAGES' ? String(imageType) : 'General');
  const formData = new FormData();
  formData.append('image', file);
  formData.append('categoryName', resolvedCategoryName);

  if (description) {
    formData.append('description', description);
  }

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(resolvedSchoolId && { schoolId: resolvedSchoolId }),
    },
  };

  const uploadUrl = '/api/images/upload/by-category';

  try {
    const response = await axiosInstance.post(uploadUrl, formData, config);
    const payload = response.data;
    return payload?.fileUrl || payload?.imageUrl || payload?.url || payload;
  } catch (error) {
    const normalizedError = error.normalizedError || normalizeApiError(error, 'Image upload failed.');
    throw new Error(normalizedError.message);
  }
};

export default axiosInstance;
